import { useEffect, useRef } from 'react';

/**
 * Panel chord mapping: press G, then the letter.
 * These map to route paths used by DashboardLayout.
 *
 * G → C : Calendar         → /dashboard/calendar
 * G → S : Scheduling       → /dashboard/scheduling
 * G → E : Entities (Master)→ /dashboard/entities
 * G → A : Assignments      → /dashboard/assignments
 * G → T : Settings (seTtings) → /dashboard/settings
 */
const PANEL_CHORDS = {
    c: '/dashboard/calendar',
    s: '/dashboard/scheduling',
    e: '/dashboard/entities',
    a: '/dashboard/assignments',
    t: '/dashboard/settings',
};

const CHORD_TIMEOUT = 800; // ms to wait for second key after G

/**
 * Returns true if the active element is an input/textarea/select
 * so that shortcuts don't fire while the user is typing.
 */
function isInputFocused() {
    const el = document.activeElement;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (el.isContentEditable) return true;
    return false;
}

/**
 * Global keyboard shortcuts hook.
 * Handles G-chord panel switching and ? cheat sheet toggle.
 *
 * Uses e.code (physical key) + e.shiftKey instead of e.key
 * to avoid keyboard layout issues with / vs ?
 */
export function useGlobalShortcuts({ onPanelSwitch, onToggleCheatSheet }) {
    const onPanelSwitchRef = useRef(onPanelSwitch);
    const onToggleCheatSheetRef = useRef(onToggleCheatSheet);
    const chordRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => { onPanelSwitchRef.current = onPanelSwitch; }, [onPanelSwitch]);
    useEffect(() => { onToggleCheatSheetRef.current = onToggleCheatSheet; }, [onToggleCheatSheet]);

    useEffect(() => {
        const handler = (e) => {
            if (isInputFocused()) return;

            // '?' for cheat sheet — strictly check e.key for layout independence
            if (e.key === '?') {
                e.preventDefault();
                e.stopImmediatePropagation();
                onToggleCheatSheetRef.current?.();
                return;
            }

            const key = e.key.toLowerCase();

            // Check if this is the second key of a G chord
            if (chordRef.current && Date.now() - chordRef.current.time < CHORD_TIMEOUT) {
                const path = PANEL_CHORDS[key];
                if (path) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    onPanelSwitchRef.current?.(path);
                }
                // Always clear chord state after second key press (whether matched or not)
                chordRef.current = null;
                clearTimeout(timerRef.current);
                return;
            }

            // Start chord with G (no modifier keys)
            if (key === 'g' && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) {
                chordRef.current = { key: 'g', time: Date.now() };
                clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    chordRef.current = null;
                }, CHORD_TIMEOUT);
                // Consume the G key so CalendarPanel doesn't process it
                e.stopImmediatePropagation();
                return;
            }
        };

        document.addEventListener('keydown', handler, true); // capture phase — runs first
        return () => {
            document.removeEventListener('keydown', handler, true);
            clearTimeout(timerRef.current);
        };
    }, []);
}


/**
 * ESC hierarchy hook.
 * Layers are processed in priority order (first = highest priority).
 * When ESC is pressed, only the highest active layer is dismissed.
 */
export function useEscapeHierarchy(layers) {
    const layersRef = useRef(layers);
    useEffect(() => { layersRef.current = layers; });

    useEffect(() => {
        const handler = (e) => {
            if (e.key !== 'Escape') return;

            const currentLayers = layersRef.current;
            if (!currentLayers) return;

            for (const layer of currentLayers) {
                if (layer.active) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    layer.onEscape();
                    return;
                }
            }
        };

        document.addEventListener('keydown', handler, true); // capture phase
        return () => document.removeEventListener('keydown', handler, true);
    }, []);
}


export { PANEL_CHORDS, isInputFocused };
